import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import './QRCart.css';

const QRCart = () => {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [table, setTable] = useState(null);
  const [specialInstructions, setSpecialInstructions] = useState('');

  useEffect(() => {
    loadCart();
    fetchTableInfo();
  }, [tableId]);

  const fetchTableInfo = async () => {
    try {
      const tableDoc = await getDoc(doc(db, 'tables', tableId));
      if (tableDoc.exists()) {
        setTable({ id: tableDoc.id, ...tableDoc.data() });
      }
    } catch (error) {
      console.error('Error fetching table:', error);
    }
  };

  const loadCart = () => {
    const savedCart = localStorage.getItem(`qr-cart-${tableId}`);
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    } else {
      navigate(`/qr/table/${tableId}`);
    }
  };

  const saveCart = (updatedCart) => {
    localStorage.setItem(`qr-cart-${tableId}`, JSON.stringify(updatedCart));
    setCart(updatedCart);
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity === 0) {
      removeItem(itemId);
      return;
    }

    const updatedCart = cart.map(item =>
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    );
    saveCart(updatedCart);
  };

  const removeItem = (itemId) => {
    const updatedCart = cart.filter(item => item.id !== itemId);
    saveCart(updatedCart);

    if (updatedCart.length === 0) {
      navigate(`/qr/table/${tableId}`);
    }
  };

  const getSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTax = () => {
    return getSubtotal() * 0.05; // 5% tax
  };

  const getTotal = () => {
    return getSubtotal() + getTax();
  };

  const handleProceedToCheckout = () => {
    localStorage.setItem(`qr-special-instructions-${tableId}`, specialInstructions);
    navigate(`/qr/checkout/${tableId}`);
  };

  if (!table || cart.length === 0) {
    return (
      <div className="loading-container">
        <div className="spinner-large"></div>
      </div>
    );
  }

  return (
    <div className="qr-cart-page">
      <div className="cart-header">
        <button className="back-btn" onClick={() => navigate(`/qr/table/${tableId}`)}>
          ← Back to Menu
        </button>
        <h1>Your Cart</h1>
        <p>Table {table.tableNumber}</p>
      </div>

      <div className="cart-content">
        <div className="cart-items">
          {cart.map(item => (
            <div key={item.id} className="cart-item">
              {item.imageUrl && (
                <img src={item.imageUrl} alt={item.name} className="cart-item-image" />
              )}
              
              <div className="cart-item-details">
                <div className="item-header">
                  <h3>{item.name}</h3>
                  {item.isVeg !== undefined && (
                    <span className={`veg-indicator-small ${item.isVeg ? 'veg' : 'non-veg'}`}>
                      {item.isVeg ? '🟢' : '🔴'}
                    </span>
                  )}
                </div>
                {item.description && (
                  <p className="item-desc-small">{item.description}</p>
                )}
                <div className="item-controls">
                  <div className="quantity-selector">
                    <button 
                      className="qty-btn"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                      min="1"
                      max="20"
                      className="qty-input"
                    />
                    <button 
                      className="qty-btn"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                  <span className="item-total">₹{item.price * item.quantity}</span>
                </div>
              </div>

              <button 
                className="btn-remove"
                onClick={() => removeItem(item.id)}
                title="Remove item"
              >
                🗑️
              </button>
            </div>
          ))}
        </div>

        <div className="special-instructions-section">
          <h3>Special Instructions (Optional)</h3>
          <textarea
            placeholder="Any dietary requirements or special requests?"
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            rows="3"
            maxLength="200"
            className="instructions-textarea"
          />
          <p className="char-count">{specialInstructions.length}/200</p>
        </div>

        <div className="cart-summary">
          <h3>Bill Summary</h3>
          <div className="summary-row">
            <span>Subtotal ({cart.length} items)</span>
            <span>₹{getSubtotal().toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>GST (5%)</span>
            <span>₹{getTax().toFixed(2)}</span>
          </div>
          <div className="summary-divider"></div>
          <div className="summary-row total-row">
            <span>Total Amount</span>
            <span>₹{getTotal().toFixed(2)}</span>
          </div>
        </div>

        <button 
          className="btn-proceed-checkout"
          onClick={handleProceedToCheckout}
        >
          Proceed to Checkout
        </button>

        <button 
          className="btn-add-more"
          onClick={() => navigate(`/qr/table/${tableId}`)}
        >
          + Add More Items
        </button>
      </div>
    </div>
  );
};

export default QRCart;
